import React, { useCallback, useEffect, useState, lazy, Suspense } from "react";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
} from "react-router-dom";
import { publicRoutes, authRoutes } from "./index";
import Login from "../Components/Home/Login";
import Header from "../Components/Header/Header";
import SideMenu from "../Components/Header/SideMenu";
import Unauthorized from "../Components/AddNewCorp/UnauthorizedUser/Unauthorized";
import Loading from "../Loading/Loading";
import { jwtDecode } from "jwt-decode";
import Cookies from "js-cookie";
import { useFirstUrl } from "../Context/FirstUrlContext";
import StudentApp from "../Components/StudentPortal/StudentApp";

// Lazy load components
const LazyLogin = lazy(() => import("../Components/Home/Login"));

const LazyUnauthorized = lazy(() =>
  import("../Components/AddNewCorp/UnauthorizedUser/Unauthorized")
);

// Helper function to check if token is valid
const isTokenValid = () => {
  const token = Cookies.get("token");
  if (!token) {
    return false;
  }
  try {
    const decoded = jwtDecode(token);
    const currentTime = Date.now() / 1000;
    return decoded.exp >= currentTime;
  } catch (error) {
    console.error("Invalid token", error);
    return false;
  }
};

const AppRoutes = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isSideMenuOpen, setIsSideMenuOpen] = useState(false);
  const [roleId, setRoleId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const history = useHistory();
  const location = useLocation();
  const { firstUrl } = useFirstUrl(); // Get firstUrl from context
  // console.log("🚀 ~ AppRoutes ~ firstUrl:", firstUrl);

  useEffect(() => {
    window.addEventListener("storage", function (e) {
      if (e.key === "login") {
        this.window.location.reload();
      }
    });
  }, []);

  useEffect(() => {
    const validRoutes = ["/addroute", "/addroute-stop"];
    if (!validRoutes.includes(location.pathname)) {
      // Preserve the cameFromLanding flag when clearing session storage
      // This prevents the flag from being cleared when navigating to /login
      const cameFromLanding = sessionStorage.getItem("cameFromLanding");
      sessionStorage.clear();
      if (cameFromLanding) {
        sessionStorage.setItem("cameFromLanding", cameFromLanding);
      }
    }
  }, [location.pathname]);

  // Redirect from /login to /home only on page refresh (not on navigation)
  // This allows users to access /login from landing page but redirects on direct URL access
  useEffect(() => {
    if (!isLoggedIn && location.pathname === "/login" && !isLoading) {
      // Check if user came from landing page by checking session storage flag
      const cameFromLanding = sessionStorage.getItem("cameFromLanding");

      // Only redirect if user didn't come from landing page (i.e., direct URL access or refresh)
      if (!cameFromLanding) {
        const timer = setTimeout(() => {
          history.push("/home");
        }, 100);
        return () => clearTimeout(timer);
      }
    }
  }, [isLoggedIn, location.pathname, history, isLoading]);

  const logoutHandler = () => {
    // Remove token and other session-related data
    Cookies.remove("token");
    localStorage.removeItem("login");
    localStorage.removeItem("userType");
    localStorage.removeItem("user");
    localStorage.removeItem("roleId");
    localStorage.removeItem("adminName");
    localStorage.removeItem("corpId");
    localStorage.removeItem("cpName");

    // Redirect to landing page
    history.push("/home");
  };

  // Check JWT token validity on mount and when location changes
  useEffect(() => {
    const token = Cookies.get("token");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        const currentTime = Date.now() / 1000;

        if (decoded.exp < currentTime) {
          // Token expired, log out and redirect
          setIsLoggedIn(false);
          setIsLoading(false);
          Cookies.remove("token");
          localStorage.setItem("login", false);
          localStorage.removeItem("userType");
          localStorage.removeItem("user");
          localStorage.removeItem("roleId");
          localStorage.removeItem("adminName");
          localStorage.removeItem("corpId");
          localStorage.removeItem("cpName");
          
          // If trying to access a protected route, redirect to home
          const isPublicRoute = publicRoutes.some(route => route.path === location.pathname);
          if (!isPublicRoute && location.pathname !== "/login") {
            history.push("/home");
          }
        } else {
          setIsLoggedIn(true);
          const storedRoleId = localStorage.getItem("roleId");
          setRoleId(storedRoleId);
          setIsLoading(false);
          
          // If logged in user tries to access /home, redirect to dashboard
          if (location.pathname === "/home") {
            const redirectUrl = firstUrl && firstUrl !== "/home" ? firstUrl : "/dashboard";
            history.replace(redirectUrl);
          }
        }
      } catch (error) {
        console.error("Invalid token", error);
        setIsLoggedIn(false);
        setIsLoading(false);
        Cookies.remove("token");
        localStorage.setItem("login", false);
        localStorage.removeItem("userType");
        localStorage.removeItem("user");
        localStorage.removeItem("roleId");
        localStorage.removeItem("adminName");
        localStorage.removeItem("corpId");
        localStorage.removeItem("cpName");

        // If trying to access a protected route, redirect to home
        const isPublicRoute = publicRoutes.some(route => route.path === location.pathname);
        if (!isPublicRoute && location.pathname !== "/login") {
          history.push("/home");
        }
      }
    } else {
      setIsLoggedIn(false);
      setIsLoading(false);
      
      // If trying to access a protected route, redirect to home
      const isPublicRoute = publicRoutes.some(route => route.path === location.pathname);
      if (!isPublicRoute && location.pathname !== "/login" && location.pathname !== "/home") {
        history.push("/home");
      }
    }
  }, [history, location.pathname, firstUrl]);

  // Check token validity every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      const token = Cookies.get("token");
      if (token) {
        try {
          const decryptedToken = token;
          const decoded = jwtDecode(decryptedToken);
          const currentTime = Date.now() / 1000;
          const storedRoleId = localStorage.getItem("roleId");
          setRoleId(storedRoleId);

          if (decoded.exp < currentTime) {
            setIsLoggedIn(false);
            Cookies.remove("token");
            localStorage.setItem("login", false);
            localStorage.removeItem("userType");
            localStorage.removeItem("user");
            localStorage.removeItem("roleId");
            localStorage.removeItem("adminName");
            localStorage.removeItem("corpId");
            localStorage.removeItem("cpName");

            // Redirect to home if on a protected route
            const isPublicRoute = publicRoutes.some(route => route.path === location.pathname);
            if (!isPublicRoute) {
              history.push("/home");
            }
          }
        } catch (error) {
          console.error("Invalid token", error);
          setIsLoggedIn(false);
          Cookies.remove("token");
          localStorage.setItem("login", false);
          localStorage.removeItem("user");
          localStorage.removeItem("userType");
          localStorage.removeItem("roleId");
          localStorage.removeItem("adminName");
          localStorage.removeItem("corpId");
          localStorage.removeItem("cpName");

          // Redirect to home if on a protected route
          const isPublicRoute = publicRoutes.some(route => route.path === location.pathname);
          if (!isPublicRoute) {
            history.push("/home");
          }
        }
      } else {
        setIsLoggedIn(false);
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [history, location.pathname]);

  const loginHandler = useCallback(
    (loggedValue) => {
      localStorage.setItem("login", true);
      setIsLoggedIn(loggedValue);
      // Redirect to dashboard if firstUrl is not set or is landing page
      // This ensures users always go to dashboard after successful login, not landing page
      const redirectUrl =
        firstUrl && firstUrl !== "/home" ? firstUrl : "/dashboard";
      history.push(redirectUrl);
    },
    [history, firstUrl]
  );

  useEffect(() => {
    if (window.screen.width >= 768) {
      const pathsElement = document.getElementsByClassName("paths")[0];
      if (pathsElement) {
        if (isSideMenuOpen) {
          pathsElement.style.width = "80%";
        } else {
          pathsElement.style.width = "100%";
        }
      }
    }
  }, [isSideMenuOpen]);

  const sideMenuHoverHandler = () => {
    setIsSideMenuOpen(true);
  };

  const sideMenuLeaveHandler = () => {
    setIsSideMenuOpen(false);
  };

  // Prevent logged-in users from accessing /home via back button or direct navigation
  useEffect(() => {
    if (isLoggedIn && location.pathname === "/home" && !isLoading) {
      const redirectUrl = firstUrl && firstUrl !== "/home" ? firstUrl : "/dashboard";
      history.replace(redirectUrl);
    }
  }, [isLoggedIn, location.pathname, isLoading, history, firstUrl]);

  // Handle browser back button - prevent navigation to /home when logged in
  useEffect(() => {
    if (!isLoggedIn || isLoading) return;

    const handlePopState = () => {
      // Small delay to check the pathname after popstate
      setTimeout(() => {
        if (window.location.pathname === "/home") {
          // Replace /home with dashboard in history
          const redirectUrl = firstUrl && firstUrl !== "/home" ? firstUrl : "/dashboard";
          history.replace(redirectUrl);
          // Push another entry to prevent going back to /home
          history.push(redirectUrl);
        }
      }, 0);
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [isLoggedIn, isLoading, history, firstUrl]);

  return (
    <div style={{ height: "100%" }}>
      <Suspense fallback={<Loading datatable="true" />}>
        {/* Render Header first if logged in and not on home */}
        {isLoggedIn && location.pathname !== "/home" && (
          <React.Fragment>
            <Header
              sideMenuOpen={sideMenuHoverHandler}
              setIsLoggedIn={setIsLoggedIn}
            />
            <div className="myContainer">
              <SideMenu
                sideMenuClose={sideMenuLeaveHandler}
                property={isSideMenuOpen}
              />
              <div className="paths">
                <Switch>
                  {/* Authenticated routes - only accessible when logged in */}
                  {authRoutes?.map((route, idx) => (
                    <Route key={idx} path={route.path}>
                      {(route.path === "/new-registration" ||
                        route.path === "/settings" ||
                        route.path === "/addpricing" ||
                        route.path === "/corporates") &&
                      roleId !== "1" ? (
                        <LazyUnauthorized />
                      ) : (
                        <route.component />
                      )}
                    </Route>
                  ))}
                  {/* Include student-staff-registration when logged in */}
                  {location.pathname === "/student-staff-registration" && (
                    <Route path="/student-staff-registration">
                      <StudentApp setIsLoggedIn={setIsLoggedIn} />
                    </Route>
                  )}
                </Switch>
              </div>
            </div>
          </React.Fragment>
        )}
        <Switch>
          <Route path="/" exact>
            <Redirect to="/home" />
          </Route>
          <Route path="/login">
            {isLoggedIn ? (
              isLoading ? (
                <Loading datatable="true" />
              ) : firstUrl ? (
                <Redirect to={firstUrl} />
              ) : (
                <Loading datatable="true" /> // wait until SideMenu sets firstUrl
              )
            ) : (
              <LazyLogin login={loginHandler} />
            )}
          </Route>
          {/* Public routes - accessible without login */}
          {publicRoutes?.map((route, idx) => (
            <Route key={idx} path={route.path} exact={route.exact}>
              {route.path === "/home" && isLoggedIn && !isLoading ? (
                <Redirect to={firstUrl && firstUrl !== "/home" ? firstUrl : "/dashboard"} />
              ) : route.path === "/student-staff-registration" && isLoggedIn ? (
                null
              ) : (
                <route.component setIsLoggedIn={setIsLoggedIn} />
              )}
            </Route>
          ))}
          {/* Catch-all route: redirect unauthenticated users trying to access protected routes */}
          <Route path="*">
            {!isLoggedIn && !isLoading ? (
              <Redirect to="/home" />
            ) : isLoading ? (
              <Loading datatable="true" />
            ) : null}
          </Route>
        </Switch>
      </Suspense>
    </div>
  );
};

export default AppRoutes;
